import { Reducer } from 'react'

import { vn } from 'vend-number'
import { SWIPER_ERROR_CODES } from '../../../../../helpers/lspayConstants'
import { UnrefRefundReason } from '../../../../../helpers/lspay.types'
import { RefundEvent, RefundState } from './types'

export const refundStateReducer: Reducer<RefundState, RefundEvent> = (
  state,
  event
) => {
  switch (event.type) {
    case 'CREATE_REFUND': {
      if (
        ![
          'unreferencedPrompt',
          'interacCardPresentPrompt',
          'overRefundPrompt',
          'declined',
          'cancelled',
          'error',
        ].includes(state.status)
      )
        return state
      return {
        status: 'creatingRefund',
        consentCollected: event.consentCollected,
        disableUnrefRefund: event.disableUnrefRefund,
      }
    }

    case 'START_POLLING': {
      if (
        !['creatingRefund', 'cancelled', 'declined', 'error'].includes(
          state.status
        )
      )
        return state
      return {
        status: 'pollingRefund',
        setupIntentId: event.setupIntentId,
      }
    }

    case 'CARD_PRESENTED': {
      if (!['pollingRefund', 'cancelled', 'error'].includes(state.status))
        return state
      return {
        status: 'pollingRefund',
        setupIntentId: event.setupIntentId,
        isCardPresented: true,
      }
    }

    case 'CANCELLED': {
      if (
        !['creatingRefund', 'pollingRefund', 'error', 'declined'].includes(
          state.status
        )
      )
        return state
      return {
        status: 'cancelled',
        setupIntentId: event.setupIntentId,
        errorMessage: event.errorMessage,
        reason: event.reason,
        reprocessingAvailable: event.reprocessingAvailable,
      }
    }
    case 'DECLINED': {
      return {
        status: 'declined',
        reason: event.reason,
        errorMessage: event.errorMessage,
        setupIntentId: event.setupIntentId,
        reprocessingAvailable: event.reprocessingAvailable,
      }
    }
    case 'ERROR': {
      if (
        ![
          'creatingRefund',
          'unreferencedPrompt',
          'pollingRefund',
          'cancelled',
        ].includes(state.status)
      )
        return state

      if (
        state.status === 'creatingRefund' &&
        event.error?.code === SWIPER_ERROR_CODES.LSPAY_CONSENT_NOT_COLLECTED
      ) {
        return {
          status: 'interacCardPresentPrompt',
        }
      }

      if (
        state.status === 'creatingRefund' &&
        event.error?.code ===
          SWIPER_ERROR_CODES.LSPAY_REF_REFUND_INSUFFICIENT_AMOUNT
      ) {
        if (state.disableUnrefRefund) {
          return {
            status: 'refundRestrictionPrompt',
            refundableAmount: vn(event.error.fields?.refundable_amount).abs(),
          }
        }
        // note: when the refundable_amount does not exist, it also goes into this block (vn(undefined).isZero() === true)
        if (vn(event.error.fields?.refundable_amount).isZero()) {
          return {
            status: 'unreferencedPrompt',
            unrefRefundReason: UnrefRefundReason.ZERO_DOLLAR_REFUNDABLE,
          }
        }

        return {
          status: 'overRefundPrompt',
          refundableAmount: vn(event.error.fields?.refundable_amount)
            .abs()
            .neg(), // always make the amount negative
        }
      }

      return {
        status: 'error',
        errorMessage: event.errorMessage,
        disableRetry: event.disableRetry,
        setupIntentId: event.setupIntentId || '',
        previousState: state,
      }
    }

    case 'COMPLETE_REFUND': {
      if (
        ![
          'creatingRefund',
          'pollingRefund',
          'cancelled',
          'declined',
          'error',
        ].includes(state.status)
      )
        return state
      return {
        status: 'done',
        transaction: event.transaction,
      }
    }

    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = event
      return _exhaustiveCheck
    }
  }
}
